// useXPGainToasts — pops a warm, skill-named toast on every user_xp change.
//
// Beginner-first copy goals:
//   - Name the *skill the user just demonstrated*, not the math.
//     "Nice — first AI image! +10" beats "+10 points · Lv 3" every time.
//   - Use the word "points" instead of "points" everywhere user-facing.
//   - Celebrate firsts loudly, repeats softly. Same action 5 times in a row
//     shouldn't feel like spam — drop to a tiny toast after the first.
//
// Why a separate global hook:
//   - Points are awarded server-side in many places (chat, image, research,
//     guide generation, quest completion, daily-hack, referral). Plumbing a
//     toast through every callsite is brittle. Realtime on user_xp is the
//     single source of truth.
//
// De-duping rules:
//   1. Skip the very first event after mount (initial fetch, not a real gain).
//   2. Suppress for ~2.5s after a quest_completion toast fires — that toast
//      already shows the points earned.
//   3. Ignore non-positive deltas (defensive; weekly_reset_at also bumps
//      updated_at).
//
// We also peek at the most recent xp_events row to figure out *what* the
// user just did so we can pick the right copy. If the lookup fails we fall
// back to a generic "Nice — +N points" toast, never a broken state.
import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { createElement } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { tierLabel } from "@/hooks/useXP";
import { fireLevelUp } from "@/lib/levelUpBus";

type XPRow = {
  total_xp: number;
  level: number;
  tier: string;
};

// Module-level so useQuestCompletionToasts can flag a recent quest toast and
// we can suppress the duplicate points toast that lands milliseconds later.
let lastQuestToastAt = 0;
export const markQuestToastFired = () => {
  lastQuestToastAt = Date.now();
};
const QUEST_SUPPRESS_WINDOW_MS = 2500;

// Per-event copy. `first` fires once-ever (no prior xp_events of this kind),
// `repeat` fires for every subsequent. Keep all strings short and warm —
// these flash for ~2.5s.
type Copy = { first: string; repeat: string };
const EVENT_COPY: Record<string, Copy> = {
  hub_chat: {
    first: "You just talked to AI. That's the whole game.",
    repeat: "Another smart prompt — keep going.",
  },
  hub_image: {
    first: "Boom — your first AI image.",
    repeat: "New image generated.",
  },
  hub_research: {
    first: "First deep research run. You're thinking like a pro.",
    repeat: "Research complete.",
  },
  guide: {
    first: "Your first AI guide — saved for life.",
    repeat: "Guide generated.",
  },
  daily_hack: {
    first: "Daily hack cleared!",
    repeat: "Hack cleared.",
  },
  referral_accepted: {
    first: "A friend just joined because of you. Legend.",
    repeat: "Another friend joined!",
  },
  quest: {
    // Quest completions usually toast separately, but keep a fallback.
    first: "Challenge complete!",
    repeat: "Challenge complete!",
  },
};
const GENERIC: Copy = {
  first: "Nice work.",
  repeat: "Nice work.",
};

const copyFor = (kind: string): Copy => EVENT_COPY[kind] ?? GENERIC;

export const useXPGainToasts = () => {
  const { user } = useAuth();
  // Last seen points — null means we haven't loaded the baseline yet.
  const lastXpRef = useRef<number | null>(null);
  const lastLevelRef = useRef<number | null>(null);
  // Track which event kinds we've already toasted at least once *this
  // session* so the "first time" copy doesn't keep repeating after a reload
  // that finds no historical match. Server-side xp_events is the source of
  // truth for true firsts; this is just a soft session guard.
  const seenKindsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user) {
      lastXpRef.current = null;
      lastLevelRef.current = null;
      seenKindsRef.current.clear();
      return;
    }

    // Seed the baseline so the first realtime event is treated as a delta,
    // not the initial value.
    (async () => {
      const { data } = await supabase
        .from("user_xp")
        .select("total_xp, level")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        lastXpRef.current = data.total_xp;
        lastLevelRef.current = data.level;
      } else {
        lastXpRef.current = 0;
        lastLevelRef.current = 1;
      }
    })();

    const channel = supabase
      .channel(`xp_gain_toasts:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "user_xp",
          filter: `user_id=eq.${user.id}`,
        },
        async (payload) => {
          const row = payload.new as XPRow | null;
          if (!row) return;
          const prev = lastXpRef.current;
          const prevLevel = lastLevelRef.current;
          // First event since mount: just record, don't toast.
          if (prev === null) {
            lastXpRef.current = row.total_xp;
            lastLevelRef.current = row.level;
            return;
          }
          const delta = row.total_xp - prev;
          lastXpRef.current = row.total_xp;
          lastLevelRef.current = row.level;
          if (delta <= 0) return;

          // Skip if a quest completion toast just fired.
          if (Date.now() - lastQuestToastAt < QUEST_SUPPRESS_WINDOW_MS) return;

          // Look up what just happened so we can name the skill.
          // Most recent xp_events row for this user — usually the one that
          // triggered this delta. Cheap single-row lookup.
          const { data: recent } = await supabase
            .from("xp_events")
            .select("kind")
            .eq("user_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();
          const kind = recent?.kind ?? "";

          // Was this the user's first ever event of this kind? Check both
          // the session ref (fast path) and a count query (truth).
          let isFirst = false;
          if (kind && !seenKindsRef.current.has(kind)) {
            const { count } = await supabase
              .from("xp_events")
              .select("id", { count: "exact", head: true })
              .eq("user_id", user.id)
              .eq("kind", kind);
            isFirst = (count ?? 0) <= 1;
            seenKindsRef.current.add(kind);
          }

          const copy = copyFor(kind);
          const headline = isFirst ? copy.first : copy.repeat;
          const pointsLine = `+${delta.toLocaleString()} points`;

          const leveledUp = prevLevel !== null && row.level > prevLevel;
          if (leveledUp) {
            // Big moment — fire the full-screen celebration (confetti + chime)
            // instead of a tiny toast. The overlay handles its own dismissal.
            fireLevelUp({
              level: row.level,
              tierLabel: tierLabel(row.tier),
              pointsGained: delta,
            });
          } else if (isFirst) {
            // Firsts get the warm headline + slightly longer dwell.
            toast.success(headline, {
              description: pointsLine,
              icon: createElement(Sparkles, { className: "w-4 h-4" }),
              duration: 4000,
            });
          } else {
            // Repeats: short, unobtrusive.
            toast(`${headline} ${pointsLine}`, {
              icon: createElement(Sparkles, { className: "w-4 h-4" }),
              duration: 2000,
            });
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
};
